import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Treemap, Sankey } from 'recharts';
import ReactFlow, { Controls, Background, MiniMap, Handle, Position, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';

const AgentOptimisation = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('discovery');
  const [showOptimizationDetails, setShowOptimizationDetails] = useState(false);

  // Codebase analysis - simulating a real AI agent codebase scan
  const codebaseAnalysis = {
    totalFiles: 127,
    totalLines: 8432,
    promptsFound: 8,
    agentFiles: 12,
    affectedFiles: 18,
    optimizationOpportunities: 24,
    
    // Prompts discovered across the codebase
    promptDistribution: [
      { location: 'src/agents/router.js', count: 2, model: 'GPT-4', complexity: 'high' },
      { location: 'src/agents/classifier.js', count: 1, model: 'Claude-3', complexity: 'medium' },
      { location: 'src/agents/sentiment.js', count: 1, model: 'Gemini-Pro', complexity: 'medium' },
      { location: 'src/agents/summarizer.js', count: 2, model: 'GPT-4/Claude-3', complexity: 'high' },
      { location: 'src/workflows/chain.js', count: 2, model: 'Mixed', complexity: 'very high' }
    ],
    
    // Interconnected prompt chains discovered
    promptChains: [
      {
        id: 'customer-support',
        name: 'Customer Support Flow',
        files: 5,
        prompts: 8,
        models: ['GPT-4', 'Claude-3', 'Gemini-Pro'],
        interconnections: 12,
        description: 'Main customer interaction pipeline with mixed model usage'
      }
    ]
  };

  // Example of discovered prompts in different files with model associations
  const discoveredPrompts = [
    {
      file: 'src/agents/router.js',
      line: 45,
      model: 'GPT-4',
      prompt: `const routingPrompt = {
  model: "gpt-4",
  temperature: 0.3,
  prompt: "Route customer query to appropriate department: support, sales, or billing"
}`,
      usage: 'Called 1,200 times/day',
      cost: '$0.03/call',
      latency: '1.2s avg',
      dependencies: ['classifier.js'],
      impact: 'Critical path - main entry point'
    },
    {
      file: 'src/agents/classifier.js',
      line: 78,
      model: 'Claude-3-Opus',
      prompt: `const classificationPrompt = {
  model: "claude-3-opus",
  temperature: 0.1,
  prompt: \`Classify customer intent:
  - technical_issue
  - billing_inquiry
  - product_question
  Return JSON with intent and confidence score\`
}`,
      usage: 'Secondary classification',
      cost: '$0.015/call',
      latency: '0.8s avg',
      dependencies: ['sentiment.js'],
      impact: 'Improves routing accuracy'
    },
    {
      file: 'src/agents/sentiment.js',
      line: 23,
      model: 'Gemini-Pro',
      prompt: `const sentimentPrompt = {
  model: "gemini-pro",
  prompt: "Analyze customer sentiment: positive, negative, neutral. Include urgency level."
}`,
      usage: 'Parallel analysis',
      cost: '$0.005/call',
      latency: '0.5s avg',
      dependencies: [],
      impact: 'Escalation decisions'
    },
    {
      file: 'src/agents/summarizer.js',
      line: 156,
      model: 'GPT-4',
      prompt: `const summaryPrompt = {
  model: "gpt-4",
  temperature: 0.7,
  prompt: "Summarize the customer interaction and provide recommended next steps"
}`,
      usage: 'End of conversation',
      cost: '$0.04/call',
      latency: '2.1s avg',
      dependencies: ['router.js', 'classifier.js'],
      impact: 'Agent handoff quality'
    },
    {
      file: 'src/agents/summarizer.js',
      line: 189,
      model: 'Claude-3-Sonnet',
      prompt: `const contextPrompt = {
  model: "claude-3-sonnet",
  prompt: "Extract key context from conversation history for personalization"
}`,
      usage: 'Context building',
      cost: '$0.008/call',
      latency: '0.6s avg',
      dependencies: [],
      impact: 'Personalization quality'
    },
    {
      file: 'src/workflows/chain.js',
      line: 234,
      model: 'Mixed',
      prompt: `const chainConfig = {
  step1: { model: "gpt-4", prompt: "Extract customer data" },
  step2: { model: "claude-3", prompt: "Validate and enrich data" },
  step3: { model: "gpt-4", prompt: "Generate response" }
}`,
      usage: 'Complex workflows',
      cost: '$0.08/chain',
      latency: '3.5s total',
      dependencies: ['All agents'],
      impact: 'End-to-end performance'
    }
  ];

  // Global optimization opportunities
  const optimizationOpportunities = [
    {
      type: 'Model Selection Optimization',
      count: 6,
      description: 'Suboptimal model choices for specific tasks',
      example: 'Using GPT-4 for simple sentiment when Gemini-Pro is 6x cheaper and faster',
      impact: 'Reduce costs by 45% while improving speed',
      currentSetup: 'GPT-4: $0.03/call, 1.2s',
      optimizedSetup: 'Gemini-Pro: $0.005/call, 0.5s',
      files: ['router.js', 'sentiment.js']
    },
    {
      type: 'Prompt Consolidation',
      count: 3,
      description: 'Multiple prompts doing similar classification tasks',
      example: 'Router and classifier prompts can be merged into one smarter prompt',
      impact: 'Eliminate 1 API call per request, save 40% latency',
      currentSetup: '2 calls: GPT-4 + Claude-3',
      optimizedSetup: '1 call: Claude-3-Opus with better prompt',
      files: ['router.js', 'classifier.js']
    },
    {
      type: 'Chain Optimization',
      count: 2,
      description: 'Sequential chains that could run in parallel',
      example: 'Sentiment analysis waits for classification to complete',
      impact: 'Reduce total latency from 3.5s to 1.8s',
      currentSetup: 'Sequential: Route → Classify → Sentiment',
      optimizedSetup: 'Parallel: Route + (Classify || Sentiment)',
      files: ['chain.js', 'workflow.js']
    },
    {
      type: 'Prompt Engineering',
      count: 8,
      description: 'Prompts not optimized for specific models',
      example: 'Claude performs better with XML tags, GPT-4 with JSON format',
      impact: 'Improve accuracy by 15-20%',
      currentSetup: 'Generic prompts across all models',
      optimizedSetup: 'Model-specific prompt formats',
      files: ['All agent files']
    },
    {
      type: 'Temperature Tuning',
      count: 5,
      description: 'Default temperature settings not optimized',
      example: 'Classification using temp=0.7 when 0.1 would be more deterministic',
      impact: 'More consistent results, 25% fewer retries',
      currentSetup: 'Default temps: 0.7-0.9',
      optimizedSetup: 'Task-specific: 0.1-0.3 for classification',
      files: ['router.js', 'classifier.js', 'sentiment.js']
    }
  ];

  // Evolution visualization data
  const evolutionData = [
    { generation: 0, prompts: 8, apiCalls: 8, latency: 3500, accuracy: 72, cost: 100, models: 3 },
    { generation: 5, prompts: 8, apiCalls: 7, latency: 2800, accuracy: 78, cost: 85, models: 3 },
    { generation: 10, prompts: 6, apiCalls: 6, latency: 2100, accuracy: 85, cost: 68, models: 3 },
    { generation: 20, prompts: 5, apiCalls: 5, latency: 1500, accuracy: 91, cost: 52, models: 2 },
    { generation: 30, prompts: 4, apiCalls: 4, latency: 1100, accuracy: 94, cost: 41, models: 2 },
    { generation: 40, prompts: 4, apiCalls: 3, latency: 800, accuracy: 96, cost: 35, models: 2 }
  ];

  // Global optimization results
  const globalOptimizationResults = {
    before: {
      totalPrompts: 8,
      totalModels: 3,
      apiCallsPerRequest: 8,
      avgLatency: 3500,
      avgCostPerRequest: 0.148,
      monthlyCode: 44400,
      errorRate: 8.2,
      modelDistribution: 'GPT-4 (50%), Claude-3 (33%), Gemini (17%)'
    },
    after: {
      totalPrompts: 4,
      totalModels: 2,
      apiCallsPerRequest: 3,
      avgLatency: 800,
      avgCostPerRequest: 0.052,
      monthlyCode: 15600,
      errorRate: 1.3,
      modelDistribution: 'Claude-3-Opus (67%), Gemini-Pro (33%)'
    }
  };

  // File dependency graph data with models
  const dependencyNodes = [
    { id: 'router', data: { label: '🔀 router.js\nGPT-4\n2 prompts' }, position: { x: 250, y: 0 } },
    { id: 'classifier', data: { label: '🏷️ classifier.js\nClaude-3-Opus\n1 prompt' }, position: { x: 100, y: 100 } },
    { id: 'sentiment', data: { label: '😊 sentiment.js\nGemini-Pro\n1 prompt' }, position: { x: 400, y: 100 } },
    { id: 'summarizer', data: { label: '📝 summarizer.js\nGPT-4 + Claude-3\n2 prompts' }, position: { x: 250, y: 200 } },
    { id: 'chain', data: { label: '🔗 chain.js\nMixed Models\n2 prompts' }, position: { x: 250, y: 300 } }
  ];

  const dependencyEdges = [
    { id: 'e1', source: 'router', target: 'classifier', animated: true, label: 'Route → Classify' },
    { id: 'e2', source: 'router', target: 'sentiment', animated: true, label: 'Parallel analysis' },
    { id: 'e3', source: 'classifier', target: 'summarizer', animated: true, label: 'Context flow' },
    { id: 'e4', source: 'sentiment', target: 'summarizer', animated: true, label: 'Sentiment data' },
    { id: 'e5', source: 'summarizer', target: 'chain', animated: true, label: 'Orchestration' }
  ];

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '12px 24px',
        background: activeTab === id ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f0f0f0',
        color: activeTab === id ? 'white' : '#333',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.3s ease'
      }}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Global Agent Optimisation with Artemis
          </h1>
          <p style={{ fontSize: '18px', color: '#666', lineHeight: '1.6' }}>
            Artemis analyzes entire codebases to find, understand, and optimize all AI agent prompts and model selections globally
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '30px', flexWrap: 'wrap' }}>
          <TabButton id="discovery" label="Codebase Discovery" icon="🔍" />
          <TabButton id="analysis" label="Global Analysis" icon="🌍" />
          <TabButton id="optimization" label="Optimization Strategy" icon="🚀" />
          <TabButton id="results" label="Results & Impact" icon="📊" />
        </div>

        {/* Tab Content */}
        {activeTab === 'discovery' && (
          <div>
            {/* Codebase Scan Results */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                🔍 Codebase Analysis Results
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{ textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>{codebaseAnalysis.totalFiles.toLocaleString()}</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Files Scanned</div>
                </div>
                <div style={{ textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e53e3e' }}>{codebaseAnalysis.promptsFound}</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Prompts Found</div>
                </div>
                <div style={{ textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#38a169' }}>{codebaseAnalysis.agentFiles}</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Agent Files</div>
                </div>
                <div style={{ textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ed8936' }}>{codebaseAnalysis.optimizationOpportunities.toLocaleString()}</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Optimizations Found</div>
                </div>
              </div>

              <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>
                Prompt Distribution Across Codebase
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={codebaseAnalysis.promptDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="location" angle={-45} textAnchor="end" height={100} fontSize={12} />
                  <YAxis />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload[0]) {
                        const data = payload[0].payload;
                        return (
                          <div style={{ background: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{data.location}</p>
                            <p style={{ margin: '0 0 5px 0' }}>Prompts: {data.count}</p>
                            <p style={{ margin: '0' }}>Model: {data.model}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" fill="#667eea" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Discovered Prompts */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                📋 Sample of Discovered Prompts
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {discoveredPrompts.map((prompt, idx) => (
                  <div 
                    key={idx}
                    style={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => setSelectedFile(prompt)}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ color: '#667eea', margin: 0 }}>
                            📁 {prompt.file}:{prompt.line}
                          </h4>
                          <span style={{
                            background: prompt.model === 'GPT-4' ? '#74aa9c' : 
                                       prompt.model === 'Claude-3-Opus' ? '#e07b39' :
                                       prompt.model === 'Claude-3-Sonnet' ? '#e09939' :
                                       prompt.model === 'Gemini-Pro' ? '#4285f4' : '#666',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {prompt.model}
                          </span>
                        </div>
                        <pre style={{
                          background: '#2d2d2d',
                          color: '#f8f8f2',
                          padding: '10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          overflow: 'auto',
                          marginBottom: '10px'
                        }}>
                          {prompt.prompt}
                        </pre>
                        <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: '#666', flexWrap: 'wrap' }}>
                          <span>📊 {prompt.usage}</span>
                          <span>💰 {prompt.cost}</span>
                          <span>⏱️ {prompt.latency}</span>
                          <span>🔗 {prompt.dependencies.join(', ')}</span>
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                          ⚡ {prompt.impact}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <p style={{ marginTop: '20px', color: '#666', fontSize: '14px', textAlign: 'center' }}>
                Showing 6 of {codebaseAnalysis.promptsFound} discovered prompts with different models...
              </p>
            </div>
          </div>
        )}

        {activeTab === 'analysis' && (
          <div>
            {/* Prompt Chains and Dependencies */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                🔗 Discovered Prompt Chains & Dependencies
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                {codebaseAnalysis.promptChains.map((chain, idx) => (
                  <div key={idx} style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <h3 style={{ color: '#667eea', marginBottom: '10px' }}>{chain.name}</h3>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>{chain.description}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                      <div>📁 <strong>{chain.files}</strong> files</div>
                      <div>💬 <strong>{chain.prompts}</strong> prompts</div>
                      <div>🔗 <strong>{chain.interconnections}</strong> connections</div>
                      <div>🎯 <strong>{(chain.prompts / chain.files).toFixed(1)}</strong> prompts/file</div>
                    </div>
                  </div>
                ))}
              </div>

              <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>
                File Dependency Graph
              </h3>
              <div style={{ height: '400px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <ReactFlow
                  nodes={dependencyNodes}
                  edges={dependencyEdges}
                  fitView
                  defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                >
                  <Background />
                  <Controls />
                  <MiniMap />
                </ReactFlow>
              </div>
            </div>

            {/* Global Optimization Opportunities */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                🎯 Global Optimization Opportunities
              </h2>
              
              {optimizationOpportunities.map((opp, idx) => (
                <div key={idx} style={{
                  background: '#fff5f5',
                  border: '1px solid #feb2b2',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '15px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ color: '#e53e3e', marginBottom: '8px' }}>
                        {opp.type} ({opp.count} instances)
                      </h3>
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>{opp.description}</p>
                      <div style={{ background: '#fff', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                        <strong>Example:</strong> {opp.example}
                      </div>
                      {opp.currentSetup && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                          <div style={{ background: '#fff5f5', padding: '8px', borderRadius: '4px', fontSize: '12px' }}>
                            <strong style={{ color: '#e53e3e' }}>Current:</strong> {opp.currentSetup}
                          </div>
                          <div style={{ background: '#f0fff4', padding: '8px', borderRadius: '4px', fontSize: '12px' }}>
                            <strong style={{ color: '#38a169' }}>Optimized:</strong> {opp.optimizedSetup}
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '20px', fontSize: '13px' }}>
                        <span style={{ color: '#38a169' }}>✅ {opp.impact}</span>
                        <span style={{ color: '#666' }}>📁 Affects: {opp.files.join(', ')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'optimization' && (
          <div>
            {/* Optimization Strategy */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                🚀 Artemis Global Optimization Strategy
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{ background: '#f0fff4', padding: '20px', borderRadius: '8px', border: '1px solid #9ae6b4' }}>
                  <h3 style={{ color: '#38a169', marginBottom: '10px' }}>1. Unified Prompt Library</h3>
                  <p style={{ fontSize: '14px', color: '#22543d' }}>
                    Consolidate 89 duplicate prompt patterns into a single optimized library. 
                    Each prompt becomes a tested, versioned component.
                  </p>
                  <div style={{ marginTop: '10px', fontSize: '13px' }}>
                    <strong>Impact:</strong> 74% reduction in prompt maintenance
                  </div>
                </div>
                
                <div style={{ background: '#e6fffa', padding: '20px', borderRadius: '8px', border: '1px solid #81e6d9' }}>
                  <h3 style={{ color: '#319795', marginBottom: '10px' }}>2. Intelligent Batching</h3>
                  <p style={{ fontSize: '14px', color: '#234e52' }}>
                    Convert sequential prompts in loops to batch operations. 
                    Process multiple items in single API calls.
                  </p>
                  <div style={{ marginTop: '10px', fontSize: '13px' }}>
                    <strong>Impact:</strong> 10x throughput improvement
                  </div>
                </div>
                
                <div style={{ background: '#fef5e7', padding: '20px', borderRadius: '8px', border: '1px solid #fad7a0' }}>
                  <h3 style={{ color: '#d68910', marginBottom: '10px' }}>3. Context Optimization</h3>
                  <p style={{ fontSize: '14px', color: '#7e5109' }}>
                    Implement global context manager to eliminate redundant context passing. 
                    Share context across related prompts.
                  </p>
                  <div style={{ marginTop: '10px', fontSize: '13px' }}>
                    <strong>Impact:</strong> 80% reduction in context tokens
                  </div>
                </div>
                
                <div style={{ background: '#f3e5f5', padding: '20px', borderRadius: '8px', border: '1px solid #ce93d8' }}>
                  <h3 style={{ color: '#8e24aa', marginBottom: '10px' }}>4. Chain Simplification</h3>
                  <p style={{ fontSize: '14px', color: '#4a148c' }}>
                    Merge multi-step chains into single, more capable prompts. 
                    Reduce API calls and latency.
                  </p>
                  <div style={{ marginTop: '10px', fontSize: '13px' }}>
                    <strong>Impact:</strong> 3x faster execution
                  </div>
                </div>
              </div>

              <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>
                Evolution Process Over 40 Generations
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="generation" label={{ value: 'Generation', position: 'insideBottom', offset: -5 }} />
                  <YAxis yAxisId="left" label={{ value: 'Count/Latency', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Accuracy %', angle: 90, position: 'insideRight' }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="apiCalls" stroke="#e53e3e" name="API Calls" strokeWidth={2} />
                  <Line yAxisId="left" type="monotone" dataKey="latency" stroke="#ed8936" name="Avg Latency (ms)" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="accuracy" stroke="#38a169" name="Accuracy %" strokeWidth={2} />
                  <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#667eea" name="Cost Index" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* How Artemis Works */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                🧬 How Artemis Optimizes at Scale
              </h2>
              
              <div style={{ display: 'grid', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    1
                  </div>
                  <div>
                    <h3 style={{ color: '#333', marginBottom: '5px' }}>Whole Codebase Analysis</h3>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      Scans entire repository to find all prompts, agent files, and their interconnections. 
                      Builds a complete dependency graph of how prompts interact.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #38a169, #48bb78)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    2
                  </div>
                  <div>
                    <h3 style={{ color: '#333', marginBottom: '5px' }}>Pattern Recognition</h3>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      Identifies duplicate logic, inefficient chains, and optimization opportunities. 
                      Understands semantic similarity even when syntax differs.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #ed8936, #f6ad55)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    3
                  </div>
                  <div>
                    <h3 style={{ color: '#333', marginBottom: '5px' }}>Global Optimization</h3>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      Optimizes the entire system holistically, not just individual prompts. 
                      Considers dependencies and ensures changes don't break interconnected flows.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #e53e3e, #fc8181)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    4
                  </div>
                  <div>
                    <h3 style={{ color: '#333', marginBottom: '5px' }}>Safe Refactoring</h3>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      Generates working code with all optimizations applied. Maintains backward compatibility 
                      and includes comprehensive tests for every change.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'results' && (
          <div>
            {/* Results Overview */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                📊 Global Optimization Results
              </h2>
              
              {/* Before/After Comparison */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '30px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: '#fff5f5',
                  padding: '25px',
                  borderRadius: '8px',
                  border: '2px solid #feb2b2'
                }}>
                  <h3 style={{ color: '#e53e3e', marginBottom: '20px' }}>❌ Before Optimization</h3>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Total Prompts:</span>
                      <strong>{globalOptimizationResults.before.totalPrompts}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Models Used:</span>
                      <strong>{globalOptimizationResults.before.totalModels} models</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>API Calls/Request:</span>
                      <strong>{globalOptimizationResults.before.apiCallsPerRequest}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Avg Latency:</span>
                      <strong>{globalOptimizationResults.before.avgLatency}ms</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Cost per Request:</span>
                      <strong>${globalOptimizationResults.before.avgCostPerRequest}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Monthly Cost:</span>
                      <strong>${globalOptimizationResults.before.monthlyCode.toLocaleString()}</strong>
                    </div>
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #feb2b2' }}>
                      <strong>Model Mix:</strong>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>{globalOptimizationResults.before.modelDistribution}</div>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  background: '#f0fff4',
                  padding: '25px',
                  borderRadius: '8px',
                  border: '2px solid #9ae6b4'
                }}>
                  <h3 style={{ color: '#38a169', marginBottom: '20px' }}>✅ After Artemis</h3>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Total Prompts:</span>
                      <strong>{globalOptimizationResults.after.totalPrompts} (-50%)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Models Used:</span>
                      <strong>{globalOptimizationResults.after.totalModels} models (-33%)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>API Calls/Request:</span>
                      <strong>{globalOptimizationResults.after.apiCallsPerRequest} (-62%)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Avg Latency:</span>
                      <strong>{globalOptimizationResults.after.avgLatency}ms (-77%)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Cost per Request:</span>
                      <strong>${globalOptimizationResults.after.avgCostPerRequest} (-65%)</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Monthly Cost:</span>
                      <strong>${globalOptimizationResults.after.monthlyCode.toLocaleString()} (-65%)</strong>
                    </div>
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #9ae6b4' }}>
                      <strong>Optimized Model Mix:</strong>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>{globalOptimizationResults.after.modelDistribution}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact Metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea20, #764ba220)',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #667eea'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#667eea' }}>$28.8K</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Annual Savings</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #38a16920, #48bb7820)',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #38a169'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#38a169' }}>4.4x</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Faster Response</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #ed893620, #f6ad5520)',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #ed8936'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ed8936' }}>84%</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Less Errors</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #e53e3e20, #fc818120)',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #e53e3e'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#e53e3e' }}>50%</div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Less Prompts</div>
                </div>
              </div>
            </div>

            {/* Real Impact */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
                💡 Real-World Impact
              </h2>
              
              <div style={{ display: 'grid', gap: '20px' }}>
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ color: '#667eea', marginBottom: '10px' }}>🚀 Performance Revolution</h3>
                  <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                    Customer support chatbot now handles 5.7x more conversations simultaneously. 
                    Response time dropped from 2.4 seconds to 420ms, creating a real-time experience 
                    that increased customer satisfaction scores by 47%.
                  </p>
                </div>
                
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ color: '#38a169', marginBottom: '10px' }}>💰 Massive Cost Savings</h3>
                  <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                    Reduced API costs by 74% through intelligent prompt consolidation and batching. 
                    The $361,000 annual savings funded two additional engineering hires to accelerate 
                    product development.
                  </p>
                </div>
                
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ color: '#ed8936', marginBottom: '10px' }}>🛠️ Developer Productivity</h3>
                  <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                    Unified prompt library eliminated duplicate work. Developers now modify prompts 
                    in one place instead of hunting through 234 files. New features ship 3x faster 
                    with standardized patterns.
                  </p>
                </div>
                
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ color: '#e53e3e', marginBottom: '10px' }}>🛡️ Reliability at Scale</h3>
                  <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                    84% reduction in errors through comprehensive error handling and fallback strategies. 
                    System uptime improved from 98.2% to 99.9%, meeting enterprise SLA requirements 
                    for the first time.
                  </p>
                </div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, #667eea20, #764ba220)',
                border: '2px solid #667eea',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px'
              }}>
                <h4 style={{ color: '#667eea', marginBottom: '10px' }}>
                  🎯 Why Artemis for Global Agent Optimization?
                </h4>
                <p style={{ color: '#333', fontSize: '14px', lineHeight: '1.6' }}>
                  Unlike tools that optimize prompts one at a time, Artemis understands your entire 
                  AI agent ecosystem. It finds patterns humans miss, optimizes globally rather than 
                  locally, and ensures every change improves the whole system. This is Software v.3 - 
                  where AI doesn't just assist development, but actively evolves and optimizes itself.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentOptimisation;